import React, { FC } from 'react';
import { Link } from 'react-router-dom';
import { Alert, Badge, Button, Card, Col, Row } from 'reactstrap';
import { DealerForm, DealerFullModel, DealerStatusTypes } from '../../../../shared/dealer';
import { RichMarkdownIt } from '../../../components';
import { useTranslation } from '../../../translations';
import { useConfig } from '../../../utils';
import { DealerButtons } from './DealerButtons';

interface AreaSelectionProps {
  readonly dealer: DealerFullModel;
  readonly form: DealerForm;
}

export const AreaSelection: FC<AreaSelectionProps> = ({ dealer, form }) => {
  const { ts } = useTranslation();
  const config = useConfig();

  return (
    <>
      <DealerButtons dealer={dealer} dealerAssistantLimit={form.dealerAssistantLimit} />
      <hr />
      {dealer.applications.length === 0 && (
        <Alert color="info">{ts('common.you_havent_yet_submitted_an')}</Alert>
      )}
      <Row>
        {config.dealersAreas.map((dealerArea) => {
          const application = dealer.applications.find((t) => t.areaId === dealerArea.id);
          let title = ts(application ? 'common.select' : 'common.create');

          if (dealerArea.full) {
            title = ts('full');
          }

          if (!dealerArea.open) {
            title = ts('closed');
          }

          return (
            <Col className="mb-10" key={dealerArea.id} lg={6} xs={12}>
              <Card className="productItem" id={`dealerArea${dealerArea.id}`}>
                <div className="productInfo">
                  <h5>{dealerArea.name}</h5>
                  {application && (
                    <StatusBadge
                      grant={!!application.paidOrderItemId}
                      status={application.status}
                    />
                  )}
                  <p>
                    {dealerArea.description ? (
                      <RichMarkdownIt source={dealerArea.description} />
                    ) : (
                      <i>{ts('common.no_description_provided')}</i>
                    )}
                  </p>
                </div>
                <div className="productPurchaseBar">
                  <Button
                    color="success"
                    disabled={!dealerArea.open}
                    id={`selectdealerArea${dealerArea.id}`}
                    tag={Link}
                    to={`/vendor/space/${dealerArea.id}`}
                  >
                    {title}
                  </Button>
                </div>
              </Card>
            </Col>
          );
        })}
      </Row>
    </>
  );
};

interface StatusBadgeProps {
  readonly status: DealerStatusTypes;
  readonly grant: boolean;
}

const StatusBadge: FC<StatusBadgeProps> = ({ status, grant }) => {
  const { ts } = useTranslation();

  switch (status) {
    case 'approved': {
      return (
        <>
          <Badge color="success" style={{ marginRight: '5px' }}>
            {ts('common.approved')}
          </Badge>
          {!grant && <Badge color="warning">{ts('common.pending_payment')}</Badge>}
        </>
      );
    }

    case 'rejected': {
      return <Badge color="warning">{ts('rejected')}</Badge>;
    }

    case 'waitlisted': {
      return <Badge color="warning">{ts('waitlisted')}</Badge>;
    }

    case 'pending': {
      return <Badge color="warning">{ts('common.pending_approval')}</Badge>;
    }

    case 'cancelled': {
      return <Badge color="secondary">{ts('common.cancelled')}</Badge>;
    }
  }
};
